import { useEffect, useState } from 'react';
import axios from 'axios';
import { DEFAULT_LIVE_WINDOW_MINUTES, DEFAULT_REFRESH_INTERVAL_MS } from './machineStatDataConstants';

const EMPTY_STATISTICS = { averageLength: null, expectedSpeedAverage: null, lengthEntries: [] };

export default function useAverageLengthStatistics({ dataset, rangeMode, from, to, liveWindowMinutes = DEFAULT_LIVE_WINDOW_MINUTES, refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS }) {
  const averageLengthIdentifier = dataset?.averageLengthIdentifier;
  const calculateExpectedSpeed = dataset?.calculateExpectedSpeed;
  const [statistics, setStatistics] = useState(EMPTY_STATISTICS);
  useEffect(() => {
    if (!averageLengthIdentifier || (rangeMode === 'range' && (!from || !to))) {
      setStatistics(EMPTY_STATISTICS);
      return undefined;
    }

    let isCancelled = false;
    setStatistics(EMPTY_STATISTICS);
    const fetchAverageLength = async () => {
      try {
        const params = {
          identifiers: averageLengthIdentifier,
          fields: calculateExpectedSpeed ? 'length,width,thickness' : 'length',
          ...(rangeMode === 'range'
            ? { from, to: calculateExpectedSpeed ? new Date(Date.parse(to) + 60_000 - 1).toISOString() : to }
            : calculateExpectedSpeed
            ? { from: new Date(Date.now() - liveWindowMinutes * 60_000).toISOString(), to: new Date().toISOString() }
            : { window_minutes: liveWindowMinutes })
        };
        const response = await axios.get('/api/v1/productionline/flowStatistic/flow_data/', { params });
        const lengthEntries = response.data ?? [];
        let totalLength = 0;
        let boardCount = 0;

        lengthEntries.forEach((entry) => {
          const length = entry?.data?.length;
          const sampleCount = entry?.sampleCount ?? 1;
          if (typeof length !== 'number' || typeof sampleCount !== 'number') return;
          totalLength += length * sampleCount;
          boardCount += sampleCount;
        });

        const rangeAverageLength = boardCount ? totalLength / boardCount : null;
        let expectedSpeedTotal = 0;
        let dimensionCount = 0;
        if (calculateExpectedSpeed && rangeAverageLength !== null) {
          lengthEntries.forEach((entry) => {
            const { width, thickness } = entry.data ?? {};
            if (!(width > 0 && thickness > 0)) return;
            const count = entry.sampleCount ?? 1;
            const target = calculateExpectedSpeed(width * thickness, rangeAverageLength);
            expectedSpeedTotal += target * count;
            dimensionCount += count;
          });
        }
        if (!isCancelled) {
          setStatistics({
            averageLength: rangeAverageLength,
            expectedSpeedAverage: dimensionCount ? expectedSpeedTotal / dimensionCount : null,
            lengthEntries: calculateExpectedSpeed ? lengthEntries : []
          });
        }
      } catch {
        if (!isCancelled) {
          setStatistics(EMPTY_STATISTICS);
        }
      }
    };

    fetchAverageLength();
    if (rangeMode !== 'live')
      return () => {
        isCancelled = true;
      };

    const timerId = window.setInterval(fetchAverageLength, refreshIntervalMs);
    return () => {
      isCancelled = true;
      window.clearInterval(timerId);
    };
  }, [averageLengthIdentifier, calculateExpectedSpeed, from, liveWindowMinutes, rangeMode, refreshIntervalMs, to]);
  return statistics;
}
